import { useState, useEffect, useCallback } from 'react';
import { hrms, getFileUrl } from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { theme } from '../../theme';

// ── Helpers for change request full-profile display ───────────────────────────
function CRInfoRow({ label, value, highlighted }) {
  if (!value && !highlighted) return null;
  return (
    <div style={{ display: 'flex', padding: '0.4rem 0', borderBottom: `1px solid ${theme.border}`, background: highlighted ? '#FFFBEB' : 'transparent' }}>
      <span style={{ width: 190, flexShrink: 0, fontSize: '0.75rem', fontWeight: 600, color: highlighted ? '#92400E' : theme.textMuted }}>{label}</span>
      <span style={{ fontSize: '0.82rem', color: highlighted ? '#92400E' : theme.text, fontWeight: highlighted ? 700 : 400 }}>{value || '—'}</span>
      {highlighted && <span style={{ marginLeft: '0.5rem', fontSize: '0.65rem', background: '#FCD34D', color: '#92400E', padding: '0.1rem 0.4rem', borderRadius: '0.25rem', fontWeight: 700, alignSelf: 'center', flexShrink: 0 }}>CHANGED</span>}
    </div>
  );
}

function CRSection({ title, icon, children }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '0.4rem 0', borderBottom: `2px solid ${theme.border}`, marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
        <span>{icon}</span>{title}
      </div>
      {children}
    </div>
  );
}

const STATUS_TABS = ['pending_review', 'approved', 'rejected'];
const STATUS_LABEL = { pending_review: 'Pending Review', approved: 'Approved', rejected: 'Rejected' };
const STATUS_COLOR = { pending_review: '#F59E0B', approved: '#10B981', rejected: theme.danger };

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', padding: '0.45rem 0', borderBottom: `1px solid ${theme.border}` }}>
      <span style={{ width: 200, flexShrink: 0, fontSize: '0.78rem', fontWeight: 600, color: theme.textMuted }}>{label}</span>
      <span style={{ fontSize: '0.875rem', color: theme.text }}>{value}</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <h4 style={{ fontSize: '0.8rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 0.5rem' }}>{title}</h4>
      {children}
    </div>
  );
}

export default function ProfileVerification() {
  const [activeTab, setActiveTab] = useState('pending_review');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [changeRequests, setChangeRequests] = useState([]);
  const [crLoading, setCrLoading] = useState(false);
  const [crRejectModal, setCrRejectModal] = useState(null);
  const [crRejectReason, setCrRejectReason] = useState('');
  const [crSaving, setCrSaving] = useState(false);
  const [expandedCrIds, setExpandedCrIds] = useState(new Set());
  const [crFullData, setCrFullData] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await hrms.getProfileReviews({ status: activeTab });
      setEmployees(r.data || []);
    } catch { setEmployees([]); }
    finally { setLoading(false); }
  }, [activeTab]);

  useEffect(() => { load(); }, [load]);

  const loadChangeRequests = useCallback(async () => {
    setCrLoading(true);
    try {
      const r = await hrms.getProfileChangeRequests('pending');
      setChangeRequests(r.data || []);
    } catch { setChangeRequests([]); } finally { setCrLoading(false); }
  }, []);

  useEffect(() => { loadChangeRequests(); }, [loadChangeRequests]);

  const handleCrApprove = async (id) => {
    setCrSaving(true);
    try { await hrms.approveProfileChangeRequest(id); loadChangeRequests(); }
    catch (e) { alert(e?.response?.data?.detail || 'Failed to approve'); }
    finally { setCrSaving(false); }
  };

  const handleCrReject = async () => {
    setCrSaving(true);
    try {
      await hrms.rejectProfileChangeRequest(crRejectModal, crRejectReason);
      setCrRejectModal(null); setCrRejectReason('');
      loadChangeRequests();
    } catch { alert('Failed to reject.'); } finally { setCrSaving(false); }
  };

  const handleApprove = async (emp) => {
    if (!window.confirm(`Approve profile for ${emp.full_name}?`)) return;
    setSaving(true);
    try {
      await hrms.approveProfile(emp.id);
      setSelected(null);
      load();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed to approve'); }
    finally { setSaving(false); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { alert('Please enter a rejection reason.'); return; }
    setSaving(true);
    try {
      await hrms.rejectProfile(rejectModal.id, { reason: rejectReason });
      setRejectModal(null);
      setRejectReason('');
      setSelected(null);
      load();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed to reject'); }
    finally { setSaving(false); }
  };

  const inp = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', boxSizing: 'border-box', resize: 'vertical', minHeight: 80 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Profile Verification</h1>
        <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>Review and approve employee profile submissions</p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: `2px solid ${theme.border}` }}>
        {STATUS_TABS.map(t => (
          <button key={t} onClick={() => setActiveTab(t)} style={{
            padding: '0.625rem 1.25rem', background: 'none', border: 'none', cursor: 'pointer',
            fontWeight: activeTab === t ? 700 : 400,
            color: activeTab === t ? STATUS_COLOR[t] : theme.textMuted,
            borderBottom: activeTab === t ? `2px solid ${STATUS_COLOR[t]}` : '2px solid transparent',
            marginBottom: '-2px', fontSize: '0.875rem',
          }}>{STATUS_LABEL[t]}</button>
        ))}
      </div>

      {loading ? (
        <p style={{ color: theme.textMuted, padding: '2rem', textAlign: 'center' }}>Loading...</p>
      ) : employees.length === 0 ? (
        <Card><p style={{ color: theme.textMuted, textAlign: 'center', padding: '2rem', margin: 0 }}>No employees in "{STATUS_LABEL[activeTab]}" status.</p></Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
          {employees.map(emp => (
            <div key={emp.id} onClick={() => setSelected(emp)}
              style={{ background: theme.cardBg, border: `1.5px solid ${selected?.id === emp.id ? theme.primary : theme.border}`, borderRadius: '0.75rem', padding: '1rem', cursor: 'pointer', transition: 'border-color 0.15s' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {emp.profile_photo
                  ? <img src={getFileUrl(emp.profile_photo)} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${theme.border}` }} />
                  : <div style={{ width: 44, height: 44, borderRadius: '50%', background: theme.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem' }}>👤</div>
                }
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: 0, fontWeight: 700, fontSize: '0.9rem', color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{emp.full_name}</p>
                  <p style={{ margin: '0.1rem 0 0', fontSize: '0.78rem', color: theme.textMuted }}>{emp.emp_code} · {emp.department_name || '—'}</p>
                </div>
              </div>
              <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.75rem', color: theme.textMuted }}>{emp.designation || emp.position_name || '—'}</span>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: '9999px', background: `${STATUS_COLOR[emp.profile_status]}18`, color: STATUS_COLOR[emp.profile_status] }}>
                  {STATUS_LABEL[emp.profile_status] || emp.profile_status}
                </span>
              </div>
              {emp.profile_status === 'rejected' && emp.profile_rejection_reason && (
                <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: theme.danger, background: '#FEF2F2', padding: '0.35rem 0.5rem', borderRadius: '0.375rem' }}>
                  Reason: {emp.profile_rejection_reason}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Detail Panel */}
      {selected && (
        <Modal isOpen={!!selected} onClose={() => setSelected(null)} title={`Profile — ${selected.full_name}`}
          footer={
            selected.profile_status === 'pending_review' ? (
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <Button variant="outlineGray" onClick={() => setSelected(null)}>Close</Button>
                <Button variant="danger" onClick={() => { setRejectModal(selected); setSelected(null); }}>Reject</Button>
                <Button variant="primary" loading={saving} onClick={() => handleApprove(selected)}>Approve Profile</Button>
              </div>
            ) : (
              <Button variant="outlineGray" onClick={() => setSelected(null)}>Close</Button>
            )
          }>
          <div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '0.25rem' }}>
            {/* Photo + header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem', padding: '0.75rem', background: theme.pageBg, borderRadius: '0.5rem' }}>
              {selected.profile_photo
                ? <img src={getFileUrl(selected.profile_photo)} alt="" style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${theme.border}` }} />
                : <div style={{ width: 64, height: 64, borderRadius: '50%', background: theme.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.75rem' }}>👤</div>
              }
              <div>
                <p style={{ margin: 0, fontWeight: 700, fontSize: '1rem', color: theme.text }}>{selected.full_name}</p>
                <p style={{ margin: '0.1rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>{selected.emp_code} · {selected.department_name} · {selected.designation || selected.position_name}</p>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: '9999px', background: `${STATUS_COLOR[selected.profile_status]}18`, color: STATUS_COLOR[selected.profile_status] }}>
                  {STATUS_LABEL[selected.profile_status]}
                </span>
              </div>
            </div>

            {selected.profile_status === 'rejected' && selected.profile_rejection_reason && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.875rem', color: theme.danger }}>
                Rejection reason: {selected.profile_rejection_reason}
              </div>
            )}

            <Section title="Personal">
              <InfoRow label="Name as per Aadhar" value={selected.full_name} />
              <InfoRow label="Father / Husband" value={selected.father_husband_name} />
              <InfoRow label="Date of Birth" value={selected.dob ? new Date(selected.dob).toLocaleDateString('en-IN') : null} />
              <InfoRow label="Gender" value={selected.gender} />
              <InfoRow label="Marital Status" value={selected.marital_status} />
              <InfoRow label="Blood Group" value={selected.blood_group} />
              <InfoRow label="Qualification" value={selected.qualification} />
              <InfoRow label="Aadhar Number" value={selected.aadhar_number} />
              <InfoRow label="Phone" value={selected.phone} />
              <InfoRow label="Alternate Phone" value={selected.alternate_phone} />
              <InfoRow label="Emergency Contact" value={selected.emergency_contact} />
              <InfoRow label="Personal Email" value={selected.personal_email} />
              <InfoRow label="Permanent Address" value={selected.address} />
              <InfoRow label="Present Address" value={selected.present_address} />
            </Section>

            {(selected.resume_path || selected.aadhar_copy_path || selected.pan_copy_path || selected.passbook_copy_path) && (
              <Section title="Documents">
                {[
                  { path: selected.resume_path,        label: 'Resume' },
                  { path: selected.aadhar_copy_path,   label: 'Aadhar Copy' },
                  { path: selected.pan_copy_path,      label: 'PAN Copy' },
                  { path: selected.passbook_copy_path, label: 'Bank Passbook' },
                ].map(({ path, label }) => path ? (
                  <div key={label} style={{ display: 'flex', padding: '0.45rem 0', borderBottom: `1px solid ${theme.border}`, alignItems: 'center' }}>
                    <span style={{ width: 200, flexShrink: 0, fontSize: '0.78rem', fontWeight: 600, color: theme.textMuted }}>{label}</span>
                    <a href={getFileUrl(path)} target="_blank" rel="noreferrer"
                      style={{ fontSize: '0.875rem', color: theme.primary, textDecoration: 'none', fontWeight: 500 }}>
                      View / Download ↗
                    </a>
                  </div>
                ) : null)}
              </Section>
            )}

            <Section title="Bank & Tax">
              <InfoRow label="PAN Number" value={selected.pan_number} />
              <InfoRow label="IFSC Code" value={selected.bank_ifsc} />
              <InfoRow label="Bank Name" value={selected.bank_name} />
              <InfoRow label="Branch" value={selected.bank_branch} />
              <InfoRow label="Account Number" value={selected.bank_account_number} />
            </Section>

            {(selected.pf_uan || selected.esic_number) && (
              <Section title="Government & Compliance">
                <InfoRow label="PF UAN" value={selected.pf_uan} />
                <InfoRow label="ESI Number" value={selected.esic_number} />
                <InfoRow label="ESI Hospital" value={selected.esi_hospital} />
              </Section>
            )}

            <Section title="Nominee">
              <InfoRow label="Nominee Name" value={selected.nominee_name} />
              <InfoRow label="Relationship" value={selected.nominee_relation} />
              <InfoRow label="Nominee Aadhar" value={selected.nominee_aadhar} />
              <InfoRow label="Nominee DOB" value={selected.nominee_dob ? new Date(selected.nominee_dob).toLocaleDateString('en-IN') : null} />
              <InfoRow label="Nominee Contact" value={selected.nominee_contact} />
            </Section>

            {selected.family_members && (() => {
              try {
                const members = JSON.parse(selected.family_members).filter(m => m.name);
                if (!members.length) return null;
                return (
                  <Section title="Family Members">
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                          {['Name', 'Relation', 'DOB', 'Aadhar'].map(h => (
                            <th key={h} style={{ padding: '0.35rem 0.5rem', textAlign: 'left', color: theme.textMuted, fontWeight: 600 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {members.map((m, i) => (
                          <tr key={i} style={{ borderBottom: `1px solid ${theme.border}` }}>
                            <td style={{ padding: '0.35rem 0.5rem' }}>{m.name}</td>
                            <td style={{ padding: '0.35rem 0.5rem' }}>{m.relation || '—'}</td>
                            <td style={{ padding: '0.35rem 0.5rem' }}>{m.dob ? new Date(m.dob).toLocaleDateString('en-IN') : '—'}</td>
                            <td style={{ padding: '0.35rem 0.5rem' }}>{m.aadhar || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Section>
                );
              } catch { return null; }
            })()}

            {/* Education */}
            {selected.education?.length > 0 && (
              <Section title="Education">
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${theme.border}`, background: theme.pageBg }}>
                      {['Degree', 'Institution', 'Field of Study', 'Year'].map(h => (
                        <th key={h} style={{ padding: '0.4rem 0.6rem', textAlign: 'left', color: theme.textMuted, fontWeight: 600, fontSize: '0.75rem' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {selected.education.map((e, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${theme.border}` }}>
                        <td style={{ padding: '0.4rem 0.6rem', fontWeight: 600, color: theme.text }}>{e.degree || '—'}</td>
                        <td style={{ padding: '0.4rem 0.6rem', color: theme.text }}>{e.institution || '—'}</td>
                        <td style={{ padding: '0.4rem 0.6rem', color: theme.textMuted }}>{e.field_of_study || '—'}</td>
                        <td style={{ padding: '0.4rem 0.6rem', color: theme.textMuted, whiteSpace: 'nowrap' }}>
                          {e.start_year && e.end_year ? `${e.start_year} – ${e.end_year}` : e.end_year || e.start_year || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>
            )}

            {/* Skills */}
            {selected.skills?.length > 0 && (
              <Section title="Skills">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {selected.skills.map((s, i) => {
                    const lvlColor = { beginner: '#6B7280', developing: '#3B82F6', proficient: '#10B981', advanced: '#F59E0B', expert: '#EF4444' };
                    const color = lvlColor[s.proficiency_level] || '#6B7280';
                    return (
                      <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.3rem 0.75rem', borderRadius: '9999px', background: `${color}18`, border: `1px solid ${color}44`, fontSize: '0.8rem', fontWeight: 600, color }}>
                        {s.skill_name}
                        {s.proficiency_level && <span style={{ fontSize: '0.68rem', opacity: 0.8, textTransform: 'capitalize' }}>· {s.proficiency_level}</span>}
                      </span>
                    );
                  })}
                </div>
              </Section>
            )}

            {/* Work Experience */}
            {selected.experience?.length > 0 && (
              <Section title="Work Experience">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {selected.experience.map((x, i) => (
                    <div key={i} style={{ padding: '0.75rem', background: theme.pageBg, borderRadius: '0.5rem', border: `1px solid ${theme.border}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.25rem' }}>
                        <div>
                          <p style={{ margin: 0, fontWeight: 700, fontSize: '0.85rem', color: theme.text }}>{x.designation || '—'}</p>
                          <p style={{ margin: '0.1rem 0 0', fontSize: '0.8rem', color: theme.primary, fontWeight: 600 }}>{x.company_name || '—'}</p>
                        </div>
                        <span style={{ fontSize: '0.75rem', color: theme.textMuted, whiteSpace: 'nowrap' }}>
                          {x.from_date ? new Date(x.from_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '—'}
                          {' – '}
                          {x.to_date ? new Date(x.to_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : 'Present'}
                        </span>
                      </div>
                      {x.description && <p style={{ margin: '0.4rem 0 0', fontSize: '0.78rem', color: theme.textMuted }}>{x.description}</p>}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Certifications */}
            {selected.certifications?.length > 0 && (
              <Section title="Certifications">
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${theme.border}`, background: theme.pageBg }}>
                      {['Certificate', 'Issuing Org', 'Issued', 'Expiry'].map(h => (
                        <th key={h} style={{ padding: '0.4rem 0.6rem', textAlign: 'left', color: theme.textMuted, fontWeight: 600, fontSize: '0.75rem' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {selected.certifications.map((c, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${theme.border}` }}>
                        <td style={{ padding: '0.4rem 0.6rem', fontWeight: 600, color: theme.text }}>{c.name || '—'}</td>
                        <td style={{ padding: '0.4rem 0.6rem', color: theme.textMuted }}>{c.issuing_org || '—'}</td>
                        <td style={{ padding: '0.4rem 0.6rem', color: theme.textMuted }}>{c.issue_date ? new Date(c.issue_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '—'}</td>
                        <td style={{ padding: '0.4rem 0.6rem', color: theme.textMuted }}>{c.expiry_date ? new Date(c.expiry_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>
            )}
          </div>
        </Modal>
      )}

      {/* ── Profile Change Requests ── */}
      {(crLoading || changeRequests.length > 0) && (
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: theme.text, margin: '0 0 1rem' }}>
            Profile Change Requests
            {changeRequests.length > 0 && (
              <span style={{ marginLeft: '0.5rem', background: '#FEF3C7', color: '#92400E', fontSize: '0.72rem', fontWeight: 700, padding: '0.1rem 0.5rem', borderRadius: '999px', verticalAlign: 'middle' }}>
                {changeRequests.length} pending
              </span>
            )}
          </h2>
          {crLoading ? (
            <p style={{ color: theme.textMuted }}>Loading...</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {changeRequests.map(req => {
                const isExpanded = expandedCrIds.has(req.id);
                const fullData = crFullData[req.employee_id];
                const changedKeys = new Set(Object.keys(req.field_changes));

                const toggleExpand = async () => {
                  const next = new Set(expandedCrIds);
                  if (next.has(req.id)) {
                    next.delete(req.id);
                  } else {
                    next.add(req.id);
                    if (!crFullData[req.employee_id]) {
                      try {
                        const [empRes, histRes] = await Promise.allSettled([
                          hrms.getEmployee(req.employee_id),
                          hrms.getEmployeeHistory(req.employee_id),
                        ]);
                        setCrFullData(prev => ({
                          ...prev,
                          [req.employee_id]: {
                            emp: empRes.status === 'fulfilled' ? empRes.value.data : null,
                            history: histRes.status === 'fulfilled' ? histRes.value.data : null,
                          },
                        }));
                      } catch {}
                    }
                  }
                  setExpandedCrIds(next);
                };

                const emp = fullData?.emp;
                const hist = fullData?.history || {};
                const fmt = d => d ? new Date(d).toLocaleDateString('en-IN') : '—';
                const cap = s => s ? s.charAt(0).toUpperCase() + s.slice(1) : '—';

                let familyMembers = [];
                try { familyMembers = JSON.parse(emp?.family_members || '[]').filter(m => m.name); } catch {}

                return (
                  <div key={req.id} style={{ background: theme.white, border: `1.5px solid ${theme.border}`, borderRadius: '0.75rem', overflow: 'hidden' }}>
                    {/* Card Header */}
                    <div style={{ padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.4rem' }}>
                          <span style={{ fontWeight: 700, fontSize: '0.9rem', color: theme.text }}>{req.employee_name}</span>
                          <span style={{ fontSize: '0.72rem', color: theme.textMuted, background: theme.pageBg, padding: '0.1rem 0.4rem', borderRadius: '999px', border: `1px solid ${theme.border}` }}>{req.emp_code}</span>
                          <span style={{ fontSize: '0.72rem', color: theme.textMuted }}>{new Date(req.requested_at).toLocaleDateString('en-IN')}</span>
                        </div>
                        {/* Changed field chips */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.25rem' }}>
                          {Object.keys(req.field_changes).map(k => (
                            <span key={k} style={{ fontSize: '0.65rem', fontWeight: 700, background: '#FEF3C7', color: '#92400E', padding: '0.15rem 0.45rem', borderRadius: '0.25rem', border: '1px solid #FCD34D', textTransform: 'uppercase' }}>
                              {k.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, alignItems: 'center' }}>
                        <button onClick={toggleExpand} style={{ background: theme.pageBg, border: `1px solid ${theme.border}`, borderRadius: '0.375rem', padding: '0.3rem 0.75rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', color: theme.text, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                          {isExpanded ? '▲ Hide Details' : '▼ View Full Profile'}
                        </button>
                        <Button size="sm" variant="success" loading={crSaving} onClick={() => handleCrApprove(req.id)}>Approve</Button>
                        <Button size="sm" variant="danger" onClick={() => { setCrRejectModal(req.id); setCrRejectReason(''); }}>Reject</Button>
                      </div>
                    </div>

                    {/* Expanded full-profile view */}
                    {isExpanded && (
                      <div style={{ borderTop: `1.5px solid ${theme.border}`, padding: '1.25rem', background: '#FAFBFD' }}>
                        {!emp ? (
                          <p style={{ color: theme.textMuted, fontSize: '0.82rem', margin: 0 }}>Loading profile…</p>
                        ) : (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>

                            {/* LEFT COLUMN */}
                            <div>
                              {/* Personal Information */}
                              <CRSection title="Personal Information" icon="👤">
                                <CRInfoRow label="Father / Husband Name"  value={req.field_changes.father_husband_name ?? emp.father_husband_name} highlighted={changedKeys.has('father_husband_name')} />
                                <CRInfoRow label="Date of Birth"          value={fmt(req.field_changes.dob ?? emp.dob)}                           highlighted={changedKeys.has('dob')} />
                                <CRInfoRow label="Gender"                 value={cap(req.field_changes.gender ?? emp.gender)}                     highlighted={changedKeys.has('gender')} />
                                <CRInfoRow label="Marital Status"         value={cap(req.field_changes.marital_status ?? emp.marital_status)}     highlighted={changedKeys.has('marital_status')} />
                                <CRInfoRow label="Blood Group"            value={req.field_changes.blood_group ?? emp.blood_group}                highlighted={changedKeys.has('blood_group')} />
                                <CRInfoRow label="Qualification"          value={req.field_changes.qualification ?? emp.qualification}            highlighted={changedKeys.has('qualification')} />
                                <CRInfoRow label="Primary Phone"          value={req.field_changes.phone ?? emp.phone}                           highlighted={changedKeys.has('phone')} />
                                <CRInfoRow label="Alternate Phone"        value={req.field_changes.alternate_phone ?? emp.alternate_phone}       highlighted={changedKeys.has('alternate_phone')} />
                                <CRInfoRow label="Personal Email"         value={req.field_changes.personal_email ?? emp.personal_email}         highlighted={changedKeys.has('personal_email')} />
                                <CRInfoRow label="Emergency Contact"      value={req.field_changes.emergency_contact ?? emp.emergency_contact}   highlighted={changedKeys.has('emergency_contact')} />
                                <CRInfoRow label="Permanent Address"      value={req.field_changes.address ?? emp.address}                       highlighted={changedKeys.has('address')} />
                                <CRInfoRow label="Present Address"        value={req.field_changes.present_address ?? emp.present_address}       highlighted={changedKeys.has('present_address')} />
                              </CRSection>

                              {/* Identity */}
                              <CRSection title="Identity Documents" icon="🪪">
                                <CRInfoRow label="Aadhar Number" value={req.field_changes.aadhar_number ?? emp.aadhar_number} highlighted={changedKeys.has('aadhar_number')} />
                                <CRInfoRow label="PAN Number"    value={req.field_changes.pan_number    ?? emp.pan_number}    highlighted={changedKeys.has('pan_number')} />
                              </CRSection>

                              {/* Bank & Tax */}
                              <CRSection title="Bank & Tax Details" icon="🏦">
                                <CRInfoRow label="IFSC Code"       value={req.field_changes.bank_ifsc           ?? emp.bank_ifsc}            highlighted={changedKeys.has('bank_ifsc')} />
                                <CRInfoRow label="Bank Name"       value={req.field_changes.bank_name           ?? emp.bank_name}            highlighted={changedKeys.has('bank_name')} />
                                <CRInfoRow label="Branch"          value={req.field_changes.bank_branch         ?? emp.bank_branch}          highlighted={changedKeys.has('bank_branch')} />
                                <CRInfoRow label="Account Number"  value={req.field_changes.bank_account_number ?? emp.bank_account_number}  highlighted={changedKeys.has('bank_account_number')} />
                              </CRSection>

                              {/* PF & ESI */}
                              <CRSection title="PF & ESI" icon="🏛️">
                                <CRInfoRow label="PF UAN"        value={req.field_changes.pf_uan       ?? emp.pf_uan}       highlighted={changedKeys.has('pf_uan')} />
                                <CRInfoRow label="ESI Number"    value={req.field_changes.esic_number  ?? emp.esic_number}  highlighted={changedKeys.has('esic_number')} />
                                <CRInfoRow label="ESI Hospital"  value={req.field_changes.esi_hospital ?? emp.esi_hospital} highlighted={changedKeys.has('esi_hospital')} />
                              </CRSection>

                              {/* Uploaded Documents */}
                              <CRSection title="Uploaded Documents" icon="📎">
                                {[
                                  { label: 'Resume',           path: emp.resume_path },
                                  { label: 'Aadhar Copy',      path: emp.aadhar_copy_path },
                                  { label: 'PAN Copy',         path: emp.pan_copy_path },
                                  { label: 'Bank Passbook',    path: emp.passbook_copy_path },
                                ].map(({ label, path }) => (
                                  <div key={label} style={{ display: 'flex', padding: '0.4rem 0', borderBottom: `1px solid ${theme.border}`, alignItems: 'center' }}>
                                    <span style={{ width: 190, flexShrink: 0, fontSize: '0.75rem', fontWeight: 600, color: theme.textMuted }}>{label}</span>
                                    {path
                                      ? <a href={getFileUrl(path)} target="_blank" rel="noreferrer" style={{ fontSize: '0.82rem', color: theme.primary, fontWeight: 500, textDecoration: 'none' }}>View / Download ↗</a>
                                      : <span style={{ fontSize: '0.78rem', color: theme.textMuted }}>Not uploaded</span>
                                    }
                                  </div>
                                ))}
                              </CRSection>
                            </div>

                            {/* RIGHT COLUMN */}
                            <div>
                              {/* Nominee */}
                              <CRSection title="Nominee Details" icon="👥">
                                <CRInfoRow label="Nominee Name"         value={req.field_changes.nominee_name     ?? emp.nominee_name}     highlighted={changedKeys.has('nominee_name')} />
                                <CRInfoRow label="Relationship"         value={req.field_changes.nominee_relation ?? emp.nominee_relation} highlighted={changedKeys.has('nominee_relation')} />
                                <CRInfoRow label="Nominee Aadhar"       value={req.field_changes.nominee_aadhar   ?? emp.nominee_aadhar}   highlighted={changedKeys.has('nominee_aadhar')} />
                                <CRInfoRow label="Nominee Date of Birth" value={fmt(req.field_changes.nominee_dob ?? emp.nominee_dob)}     highlighted={changedKeys.has('nominee_dob')} />
                                <CRInfoRow label="Nominee Contact"      value={req.field_changes.nominee_contact  ?? emp.nominee_contact}  highlighted={changedKeys.has('nominee_contact')} />
                              </CRSection>

                              {/* Family Members */}
                              <CRSection title="Family Members" icon="🏠">
                                {familyMembers.length === 0 ? (
                                  <p style={{ fontSize: '0.78rem', color: theme.textMuted, margin: '0.25rem 0' }}>No family members added.</p>
                                ) : (
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                                    <thead>
                                      <tr style={{ background: theme.pageBg }}>
                                        {['Name', 'Relation', 'DOB', 'Aadhar'].map(h => (
                                          <th key={h} style={{ padding: '0.3rem 0.5rem', textAlign: 'left', color: theme.textMuted, fontWeight: 600, fontSize: '0.7rem', borderBottom: `1px solid ${theme.border}` }}>{h}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {familyMembers.map((m, i) => (
                                        <tr key={i} style={{ borderBottom: `1px solid ${theme.border}` }}>
                                          <td style={{ padding: '0.3rem 0.5rem' }}>{m.name || '—'}</td>
                                          <td style={{ padding: '0.3rem 0.5rem' }}>{m.relation || '—'}</td>
                                          <td style={{ padding: '0.3rem 0.5rem' }}>{m.dob ? fmt(m.dob) : '—'}</td>
                                          <td style={{ padding: '0.3rem 0.5rem' }}>{m.aadhar || '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </CRSection>

                              {/* Education */}
                              <CRSection title="Education" icon="🎓">
                                {(hist.education || []).length === 0 ? (
                                  <p style={{ fontSize: '0.78rem', color: theme.textMuted, margin: '0.25rem 0' }}>No education records.</p>
                                ) : (hist.education || []).map((e, i) => (
                                  <div key={i} style={{ padding: '0.4rem 0', borderBottom: `1px solid ${theme.border}` }}>
                                    <p style={{ margin: 0, fontWeight: 700, fontSize: '0.8rem', color: theme.text }}>{e.degree}{e.field_of_study ? ` — ${e.field_of_study}` : ''}</p>
                                    <p style={{ margin: '0.1rem 0 0', fontSize: '0.75rem', color: theme.textMuted }}>{e.institution}{e.end_year ? ` · ${e.end_year}` : ''}</p>
                                  </div>
                                ))}
                              </CRSection>

                              {/* Skills */}
                              <CRSection title="Skills" icon="⚡">
                                {(hist.skills || []).length === 0 ? (
                                  <p style={{ fontSize: '0.78rem', color: theme.textMuted, margin: '0.25rem 0' }}>No skills added.</p>
                                ) : (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', paddingTop: '0.25rem' }}>
                                    {(hist.skills || []).map((s, i) => {
                                      const lvlColor = { beginner: '#6B7280', intermediate: '#3B82F6', advanced: '#F59E0B', expert: '#EF4444' };
                                      const c = lvlColor[s.proficiency_level] || '#6B7280';
                                      return (
                                        <span key={i} style={{ fontSize: '0.72rem', fontWeight: 600, background: `${c}15`, color: c, border: `1px solid ${c}40`, padding: '0.15rem 0.5rem', borderRadius: '0.25rem' }}>
                                          {s.skill_name} · {s.proficiency_level}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                              </CRSection>

                              {/* Certifications */}
                              <CRSection title="Certifications" icon="🏅">
                                {(hist.certifications || []).length === 0 ? (
                                  <p style={{ fontSize: '0.78rem', color: theme.textMuted, margin: '0.25rem 0' }}>No certifications.</p>
                                ) : (hist.certifications || []).map((c, i) => (
                                  <div key={i} style={{ padding: '0.4rem 0', borderBottom: `1px solid ${theme.border}` }}>
                                    <p style={{ margin: 0, fontWeight: 700, fontSize: '0.8rem', color: theme.text }}>{c.name}</p>
                                    <p style={{ margin: '0.1rem 0 0', fontSize: '0.75rem', color: theme.textMuted }}>
                                      {c.issuing_org}{c.issue_date ? ` · Issued: ${fmt(c.issue_date)}` : ''}{c.expiry_date ? ` · Expires: ${fmt(c.expiry_date)}` : ''}
                                    </p>
                                  </div>
                                ))}
                              </CRSection>

                              {/* Work Experience */}
                              <CRSection title="Work Experience" icon="💼">
                                {(hist.experience || []).length === 0 ? (
                                  <p style={{ fontSize: '0.78rem', color: theme.textMuted, margin: '0.25rem 0' }}>No experience records.</p>
                                ) : (hist.experience || []).map((x, i) => (
                                  <div key={i} style={{ padding: '0.4rem 0', borderBottom: `1px solid ${theme.border}` }}>
                                    <p style={{ margin: 0, fontWeight: 700, fontSize: '0.8rem', color: theme.text }}>{x.designation}</p>
                                    <p style={{ margin: '0.1rem 0 0', fontSize: '0.75rem', color: theme.primary, fontWeight: 600 }}>{x.company_name}</p>
                                    <p style={{ margin: '0.1rem 0 0', fontSize: '0.72rem', color: theme.textMuted }}>
                                      {x.from_date ? fmt(x.from_date) : ''}{' – '}{x.to_date ? fmt(x.to_date) : 'Present'}
                                    </p>
                                  </div>
                                ))}
                              </CRSection>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* CR Reject Modal */}
      <Modal isOpen={!!crRejectModal} onClose={() => { setCrRejectModal(null); setCrRejectReason(''); }} title="Reject Change Request"
        footer={<><Button variant="outlineGray" onClick={() => setCrRejectModal(null)}>Cancel</Button><Button variant="danger" loading={crSaving} onClick={handleCrReject}>Reject</Button></>}>
        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.35rem' }}>Reason (optional)</label>
        <textarea style={inp} value={crRejectReason} onChange={e => setCrRejectReason(e.target.value)} placeholder="Reason for rejection..." />
      </Modal>

      {/* Reject Modal */}
      <Modal isOpen={!!rejectModal} onClose={() => { setRejectModal(null); setRejectReason(''); }} title="Reject Profile"
        footer={
          <>
            <Button variant="outlineGray" onClick={() => { setRejectModal(null); setRejectReason(''); }}>Cancel</Button>
            <Button variant="danger" loading={saving} onClick={handleReject}>Reject Profile</Button>
          </>
        }>
        <p style={{ fontSize: '0.875rem', color: theme.text, marginBottom: '0.75rem' }}>
          Rejecting profile for <strong>{rejectModal?.full_name}</strong>. The employee will be notified to re-submit with corrections.
        </p>
        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.4rem' }}>Reason for Rejection *</label>
        <textarea style={inp} value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="e.g. Aadhar number is incorrect, bank account number missing..." />
      </Modal>
    </div>
  );
}
